import { useState } from "react";
import { Ban, CheckCircle2, Loader2, Users } from "lucide-react";
import type { AdminUserRow } from "@/hooks/use-access";
import { useSetUserActive } from "@/hooks/use-access";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  users?: AdminUserRow[];
  isLoading: boolean;
  /** id (keycloak sub) del operador, para no dejar que se desactive a sí mismo. */
  currentUserId?: string;
}

function formatDate(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return date.toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatusBadge({ user }: { user: AdminUserRow }) {
  if (!user.is_active) {
    return (
      <span className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
        Desactivado
      </span>
    );
  }
  if (user.never_logged_in) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        Nunca ha entrado
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
      Activo
    </span>
  );
}

export function UsersAccessTable({ users, isLoading, currentUserId }: Props) {
  const setActive = useSetUserActive();
  const [pendingUser, setPendingUser] = useState<AdminUserRow | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (!users?.length) {
    return (
      <EmptyState
        icon={<Users />}
        title="Sin usuarios"
        description="Todavía no hay usuarios registrados en la plataforma."
      />
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Usuario</th>
              <th className="px-4 py-3 text-left font-medium">Estado</th>
              <th className="px-4 py-3 text-left font-medium">Último acceso</th>
              <th className="px-4 py-3 text-right font-medium">Accesos 30d</th>
              <th className="px-4 py-3 text-right font-medium">Descargas 30d</th>
              <th className="px-4 py-3 text-right font-medium">Coste IA 30d</th>
              <th className="px-4 py-3 text-right font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((user) => {
              const isSelf = !!currentUserId && user.id === currentUserId;
              const isPending =
                setActive.isPending && setActive.variables?.userId === user.id;

              return (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{user.name || user.email}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge user={user} />
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(user.last_seen_at)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{user.events_30d ?? 0}</td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {user.documents_downloaded_30d ?? 0}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    ${(user.ai_cost_usd_30d ?? 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {user.is_active ? (
                      <button
                        type="button"
                        onClick={() => setPendingUser(user)}
                        disabled={isSelf || isPending}
                        title={
                          isSelf
                            ? "No puedes desactivar tu propia cuenta"
                            : "Quitar el acceso a este usuario"
                        }
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        {isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Ban className="h-3.5 w-3.5" />
                        )}
                        Desactivar
                      </button>
                    ) : (
                      <button
                        type="button"
                        // Reactivar no es destructivo: sin confirmación. Un
                        // diálogo aquí solo entrena a confirmar sin leer.
                        onClick={() =>
                          setActive.mutate({ userId: user.id, isActive: true })
                        }
                        disabled={isPending}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-40"
                      >
                        {isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        Activar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!pendingUser}
        onOpenChange={(open) => !open && setPendingUser(null)}
        onConfirm={() => {
          if (pendingUser) {
            setActive.mutate({ userId: pendingUser.id, isActive: false });
          }
          setPendingUser(null);
        }}
        title={`Desactivar a ${pendingUser?.name || pendingUser?.email || ""}`}
        description="Perderá el acceso en su siguiente acción. Sus licitaciones y propuestas no se borran, y puedes reactivarlo cuando quieras."
        confirmLabel="Desactivar"
        variant="destructive"
        loading={setActive.isPending}
      />
    </>
  );
}
